/**
 * V51 — Country → IANA timezone database for the Foreign/NRI demo form.
 *
 * One entry per country (or major sub-zone where it matters — US, Canada,
 * Australia). Each entry carries:
 *   • code        — ISO 3166-1 alpha-2 (used to render the flag emoji)
 *   • name        — display name
 *   • tz          — IANA timezone identifier
 *   • abbrFallback— short label (AEST, EST, …) used when the platform
 *                   doesn't surface a short name via Intl
 *
 * UTC offset and abbreviation are computed at runtime from `tz` so DST is
 * always handled correctly (no manual summer/winter tables).
 */

export const COUNTRY_TIMEZONES = [
    // South Asia / India anchor
    { code: "IN", name: "India", tz: "Asia/Kolkata", abbrFallback: "IST" },

    // Middle East
    { code: "AE", name: "United Arab Emirates", tz: "Asia/Dubai", abbrFallback: "GST" },
    { code: "SA", name: "Saudi Arabia", tz: "Asia/Riyadh", abbrFallback: "AST" },
    { code: "QA", name: "Qatar", tz: "Asia/Qatar", abbrFallback: "AST" },
    { code: "OM", name: "Oman", tz: "Asia/Muscat", abbrFallback: "GST" },
    { code: "KW", name: "Kuwait", tz: "Asia/Kuwait", abbrFallback: "AST" },
    { code: "BH", name: "Bahrain", tz: "Asia/Bahrain", abbrFallback: "AST" },

    // South-East Asia
    { code: "SG", name: "Singapore", tz: "Asia/Singapore", abbrFallback: "SGT" },
    { code: "MY", name: "Malaysia", tz: "Asia/Kuala_Lumpur", abbrFallback: "MYT" },
    { code: "TH", name: "Thailand", tz: "Asia/Bangkok", abbrFallback: "ICT" },
    { code: "PH", name: "Philippines", tz: "Asia/Manila", abbrFallback: "PST" },
    { code: "ID", name: "Indonesia (Jakarta)", tz: "Asia/Jakarta", abbrFallback: "WIB" },
    { code: "VN", name: "Vietnam", tz: "Asia/Ho_Chi_Minh", abbrFallback: "ICT" },

    // East Asia
    { code: "CN", name: "China", tz: "Asia/Shanghai", abbrFallback: "CST" },
    { code: "HK", name: "Hong Kong", tz: "Asia/Hong_Kong", abbrFallback: "HKT" },
    { code: "JP", name: "Japan", tz: "Asia/Tokyo", abbrFallback: "JST" },
    { code: "KR", name: "South Korea", tz: "Asia/Seoul", abbrFallback: "KST" },
    { code: "TW", name: "Taiwan", tz: "Asia/Taipei", abbrFallback: "CST" },

    // Oceania
    { code: "AU", name: "Australia (Sydney / Melbourne)", tz: "Australia/Sydney", abbrFallback: "AEST" },
    { code: "AU", name: "Australia (Perth)", tz: "Australia/Perth", abbrFallback: "AWST" },
    { code: "AU", name: "Australia (Adelaide)", tz: "Australia/Adelaide", abbrFallback: "ACST" },
    { code: "AU", name: "Australia (Brisbane)", tz: "Australia/Brisbane", abbrFallback: "AEST" },
    { code: "NZ", name: "New Zealand", tz: "Pacific/Auckland", abbrFallback: "NZST" },
    { code: "FJ", name: "Fiji", tz: "Pacific/Fiji", abbrFallback: "FJT" },

    // North America (sub-zones — DST varies regionally)
    { code: "US", name: "USA — New York / Eastern", tz: "America/New_York", abbrFallback: "EST" },
    { code: "US", name: "USA — Chicago / Central", tz: "America/Chicago", abbrFallback: "CST" },
    { code: "US", name: "USA — Denver / Mountain", tz: "America/Denver", abbrFallback: "MST" },
    { code: "US", name: "USA — Los Angeles / Pacific", tz: "America/Los_Angeles", abbrFallback: "PST" },
    { code: "US", name: "USA — Phoenix (no DST)", tz: "America/Phoenix", abbrFallback: "MST" },
    { code: "US", name: "USA — Honolulu", tz: "Pacific/Honolulu", abbrFallback: "HST" },
    { code: "CA", name: "Canada — Toronto / Eastern", tz: "America/Toronto", abbrFallback: "EST" },
    { code: "CA", name: "Canada — Vancouver / Pacific", tz: "America/Vancouver", abbrFallback: "PST" },
    { code: "CA", name: "Canada — Calgary / Mountain", tz: "America/Edmonton", abbrFallback: "MST" },

    // Europe
    { code: "GB", name: "United Kingdom (London)", tz: "Europe/London", abbrFallback: "GMT" },
    { code: "IE", name: "Ireland", tz: "Europe/Dublin", abbrFallback: "GMT" },
    { code: "DE", name: "Germany", tz: "Europe/Berlin", abbrFallback: "CET" },
    { code: "FR", name: "France", tz: "Europe/Paris", abbrFallback: "CET" },
    { code: "IT", name: "Italy", tz: "Europe/Rome", abbrFallback: "CET" },
    { code: "ES", name: "Spain", tz: "Europe/Madrid", abbrFallback: "CET" },
    { code: "NL", name: "Netherlands", tz: "Europe/Amsterdam", abbrFallback: "CET" },
    { code: "BE", name: "Belgium", tz: "Europe/Brussels", abbrFallback: "CET" },
    { code: "CH", name: "Switzerland", tz: "Europe/Zurich", abbrFallback: "CET" },
    { code: "AT", name: "Austria", tz: "Europe/Vienna", abbrFallback: "CET" },
    { code: "SE", name: "Sweden", tz: "Europe/Stockholm", abbrFallback: "CET" },
    { code: "NO", name: "Norway", tz: "Europe/Oslo", abbrFallback: "CET" },
    { code: "DK", name: "Denmark", tz: "Europe/Copenhagen", abbrFallback: "CET" },
    { code: "FI", name: "Finland", tz: "Europe/Helsinki", abbrFallback: "EET" },
    { code: "PT", name: "Portugal", tz: "Europe/Lisbon", abbrFallback: "WET" },
    { code: "PL", name: "Poland", tz: "Europe/Warsaw", abbrFallback: "CET" },
    { code: "RU", name: "Russia (Moscow)", tz: "Europe/Moscow", abbrFallback: "MSK" },
    { code: "TR", name: "Turkey", tz: "Europe/Istanbul", abbrFallback: "TRT" },

    // Africa
    { code: "ZA", name: "South Africa", tz: "Africa/Johannesburg", abbrFallback: "SAST" },
    { code: "KE", name: "Kenya", tz: "Africa/Nairobi", abbrFallback: "EAT" },
    { code: "NG", name: "Nigeria", tz: "Africa/Lagos", abbrFallback: "WAT" },
    { code: "EG", name: "Egypt", tz: "Africa/Cairo", abbrFallback: "EET" },
    { code: "MA", name: "Morocco", tz: "Africa/Casablanca", abbrFallback: "WET" },
    { code: "TZ", name: "Tanzania", tz: "Africa/Dar_es_Salaam", abbrFallback: "EAT" },
    { code: "UG", name: "Uganda", tz: "Africa/Kampala", abbrFallback: "EAT" },
    { code: "GH", name: "Ghana", tz: "Africa/Accra", abbrFallback: "GMT" },

    // South America
    { code: "BR", name: "Brazil (São Paulo)", tz: "America/Sao_Paulo", abbrFallback: "BRT" },
    { code: "AR", name: "Argentina", tz: "America/Argentina/Buenos_Aires", abbrFallback: "ART" },
    { code: "CL", name: "Chile", tz: "America/Santiago", abbrFallback: "CLT" },
    { code: "MX", name: "Mexico City", tz: "America/Mexico_City", abbrFallback: "CST" },
];

// ISO-2 country code → flag emoji (regional indicator pairs)
export function flagEmoji(code) {
    if (!code || code.length !== 2) return "🌐";
    const A = 0x1f1e6;
    return String.fromCodePoint(...code.toUpperCase().split("").map((c) => A + c.charCodeAt(0) - 65));
}

/**
 * V128 — Default COUNTRY_TIMEZONES row for a given ISO-2 country code.
 *
 * For single-timezone countries (SG, AE, UK, DE, JP, …) returns the single
 * matching row. For multi-timezone countries (US, AU, CA) returns the FIRST
 * row, which is the most populated / most commonly-used timezone:
 *   • US → America/New_York (Eastern)
 *   • CA → America/Toronto (Eastern)
 *   • AU → Australia/Sydney (AEST)
 * The parent can override to a sub-zone via the timezone dropdown at any time.
 *
 * Returns `null` when the ISO is not in our list (very rare countries
 * where we haven't seeded a timezone entry) — the caller should leave the
 * timezone unset and let the parent pick manually.
 */
export function defaultTimezoneForCountry(iso2) {
    if (!iso2 || typeof iso2 !== "string") return null;
    const target = iso2.toUpperCase();
    return COUNTRY_TIMEZONES.find((c) => c.code === target) || null;
}

/**
 * V129 — How many distinct sub-zones a country has in COUNTRY_TIMEZONES.
 * Used by the "Pick your specific sub-zone" nudge chip on the Foreign flow —
 * we only nudge when a family lives in a country with multiple sub-zones
 * (US:6, AU:4, CA:3) so single-TZ-country parents don't see an unnecessary
 * disambiguation cue.
 */
export function countTimezonesForCountry(iso2) {
    if (!iso2 || typeof iso2 !== "string") return 0;
    const target = iso2.toUpperCase();
    return COUNTRY_TIMEZONES.reduce((n, c) => (c.code === target ? n + 1 : n), 0);
}

// Compute UTC offset (e.g. "UTC+5:30") for an IANA timezone at the given date
export function getUtcOffset(tz, date = new Date()) {
    try {
        // Use Intl with 'longOffset' which returns strings like 'GMT+5:30'
        const fmt = new Intl.DateTimeFormat("en-GB", { timeZone: tz, timeZoneName: "longOffset" });
        const parts = fmt.formatToParts(date);
        const off = parts.find((p) => p.type === "timeZoneName")?.value || "";
        // 'GMT+5:30' → 'UTC+5:30', also handle 'GMT' alone
        return off.replace(/^GMT/, "UTC") || "UTC";
    } catch (e) {
        return "UTC";
    }
}

// Short abbreviation (EST, GMT, AEST) for an IANA timezone at the given date
export function getTzAbbreviation(tz, date = new Date(), fallback = "") {
    try {
        const fmt = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" });
        const parts = fmt.formatToParts(date);
        const abbr = parts.find((p) => p.type === "timeZoneName")?.value || "";
        // If the platform falls back to "GMT+10" or similar, prefer our friendly abbr
        if (/^[A-Z]{2,5}$/.test(abbr)) return abbr;
        return fallback || abbr;
    } catch (e) {
        return fallback || "";
    }
}

/**
 * Convert a local YYYY-MM-DD + HH:mm (24h) in `fromTz` to the equivalent
 * wall-clock time in `toTz`. Returns { date, hh, mm, ampm, formatted } or null.
 *
 * Strategy: build a UTC instant by inverting the offset of `fromTz` for the
 * given local datetime, then format that instant in `toTz`. Uses only the
 * platform's Intl + IANA database — DST is automatic.
 */
export function convertTime(localDate, localTime24, fromTz, toTz = "Asia/Kolkata") {
    if (!localDate || !localTime24 || !fromTz) return null;
    const [Y, M, D] = localDate.split("-").map((n) => parseInt(n, 10));
    const [h, m] = localTime24.split(":").map((n) => parseInt(n, 10));
    if (!Y || !M || !D || Number.isNaN(h) || Number.isNaN(m)) return null;

    // Build the UTC instant whose wall-clock in fromTz matches Y-M-D h:m.
    // Start with a naive UTC, then iteratively correct the offset (one pass
    // is enough for non-pathological DST transitions).
    let guess = Date.UTC(Y, M - 1, D, h, m, 0);
    for (let i = 0; i < 2; i++) {
        const parts = new Intl.DateTimeFormat("en-GB", {
            timeZone: fromTz,
            year: "numeric", month: "2-digit", day: "2-digit",
            hour: "2-digit", minute: "2-digit", hour12: false,
        }).formatToParts(new Date(guess));
        const pY = +parts.find((p) => p.type === "year").value;
        const pM = +parts.find((p) => p.type === "month").value;
        const pD = +parts.find((p) => p.type === "day").value;
        const pH = +parts.find((p) => p.type === "hour").value;
        const pMi = +parts.find((p) => p.type === "minute").value;
        const localAsUtc = Date.UTC(pY, pM - 1, pD, pH, pMi, 0);
        const diff = Date.UTC(Y, M - 1, D, h, m, 0) - localAsUtc;
        if (diff === 0) break;
        guess += diff;
    }
    const instant = new Date(guess);

    // Format that instant in `toTz`
    const fmt = new Intl.DateTimeFormat("en-US", {
        timeZone: toTz,
        weekday: "short", year: "numeric", month: "short", day: "numeric",
        hour: "numeric", minute: "2-digit", hour12: true,
    });
    return {
        iso: instant.toISOString(),
        formatted: fmt.format(instant),
        abbr: getTzAbbreviation(toTz, instant, "IST"),
        offset: getUtcOffset(toTz, instant),
    };
}

/**
 * Convenience: format 24h "HH:mm" → "h:mm AM/PM"
 */
export function to12Hour(hhmm) {
    if (!hhmm) return "";
    const [h, m] = hhmm.split(":").map((n) => parseInt(n, 10));
    if (Number.isNaN(h)) return "";
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = ((h + 11) % 12) + 1;
    return `${h12}:${String(m || 0).padStart(2, "0")} ${ampm}`;
}
